/**
 * Helpers for validating and reasoning about a target's optional table prefix.
 *
 * A table prefix is prepended to every physical table name a profile creates in the
 * target database (both the final table and its <c>stage_</c> staging counterpart).
 * Because SQL Server identifiers are capped at 128 characters and the staging table
 * carries an additional <c>stage_</c> prefix, the effective budget for
 * <c>prefix + logicalName</c> must account for that longest form.
 */

/** SQL Server maximum identifier length (sysname). */
const MAX_SQL_IDENTIFIER_LENGTH = 128;

/** Fixed prefix applied to staging tables, in addition to any configured table prefix. */
const STAGING_TABLE_PREFIX = 'stage_';

/**
 * Pattern for a valid table prefix: letters, digits and underscores only. An empty
 * string is treated as "no prefix" and is validated separately (it is always allowed).
 */
const TABLE_PREFIX_PATTERN = /^[A-Za-z0-9_]+$/;

/**
 * Validates a table prefix value. Empty/whitespace is allowed (means no prefix).
 * @param prefix The raw prefix entered by the user.
 * @returns True when the prefix is empty or a safe identifier fragment.
 */
export function isValidTablePrefix(prefix: string): boolean {
  const trimmed = prefix.trim();
  return trimmed.length === 0 || TABLE_PREFIX_PATTERN.test(trimmed);
}

/**
 * Determines whether a logical table name would exceed the SQL identifier limit once
 * the prefix (and the fixed staging prefix, the longest resulting form) are applied.
 * @param prefix The table prefix (may be empty).
 * @param logicalName The logical source-table name.
 * @returns True when the resulting physical name would be too long to create.
 */
export function exceedsIdentifierLimit(prefix: string, logicalName: string): boolean {
  const longestLength = STAGING_TABLE_PREFIX.length + prefix.trim().length + logicalName.length;
  return longestLength > MAX_SQL_IDENTIFIER_LENGTH;
}
